import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { Loader2, Search, UserCheck, X, RefreshCw, ArrowLeft, AlertTriangle, HelpCircle } from "lucide-react";

/**
 * Manual validation queue for imported RDV.
 * Shows TO_VALIDATE + UNMATCHED rows with simple per-row actions:
 *   - Associer  → modal patient search → POST /connector/match
 *   - Ignorer   → POST /connector/ignore
 *
 * Roles allowed (per backend RBAC):
 *   ADMIN_CLINIQUE, SUPER_ADMIN_SAAS, MEDECIN, IDE, SECRETAIRE_MEDICAL
 */
export default function AppointmentsValidationPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");   // "" = both
  const [loading, setLoading] = useState(false);
  const [matchTarget, setMatchTarget] = useState(null);   // appt being matched

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/appointments/connector/pending", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger la file de validation");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleIgnore = async (appt) => {
    if (!window.confirm(`Ignorer le RDV ${appt.externalId} ?\nIl ne sera plus traité automatiquement.`)) return;
    try {
      await api.post("/appointments/connector/ignore", { appointmentId: appt.appointmentId });
      toast.success(`RDV ${appt.externalId} ignoré`);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'opération");
    }
  };

  const handleMatchSelected = async (appt, patientId) => {
    try {
      await api.post("/appointments/connector/match", { appointmentId: appt.appointmentId, patientId });
      toast.success(`RDV associé`);
      setMatchTarget(null);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'association");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  const statusBadge = (s) => {
    const map = {
      TO_VALIDATE: { bg: "#fef3c7", fg: "#92400e", label: "À valider", icon: <HelpCircle size={14} /> },
      UNMATCHED:   { bg: "#fee2e2", fg: "#991b1b", label: "Non trouvé",  icon: <AlertTriangle size={14} /> },
    };
    const m = map[s] || { bg: "#e5e7eb", fg: "#374151", label: s };
    return (
      <span data-testid={`status-badge-${s}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, background: m.bg, color: m.fg,
                     padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
        {m.icon}{m.label}
      </span>
    );
  };

  return (
    <div data-testid="appt-validation-page" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
            <ArrowLeft size={16} /> Retour
          </button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Validation des RDV importés</h1>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 14 }}>
            Associer ou ignorer les rendez-vous importés que le système n'a pas pu rattacher automatiquement.
          </p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={fetchPending}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #d1d5db",
                   background: "white", borderRadius: 8, cursor: "pointer", fontSize: 14 }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { v: "", label: `Tous (${total})` },
          { v: "TO_VALIDATE", label: "À valider" },
          { v: "UNMATCHED", label: "Non trouvés" },
        ].map((opt) => (
          <button
            key={opt.v}
            data-testid={`filter-${opt.v || "all"}`}
            onClick={() => setStatusFilter(opt.v)}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid",
                     borderColor: statusFilter === opt.v ? "#2563eb" : "#d1d5db",
                     background: statusFilter === opt.v ? "#dbeafe" : "white",
                     color: statusFilter === opt.v ? "#1e40af" : "#374151",
                     cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <table data-testid="pending-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr style={{ textAlign: "left" }}>
              <th style={th}>Nom importé</th>
              <th style={th}>Téléphone</th>
              <th style={th}>Date RDV</th>
              <th style={th}>Statut</th>
              <th style={th}>Raison</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#6b7280" }}>
                Aucun RDV à valider.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.appointmentId} data-testid={`row-${it.externalId}`}
                  style={{ borderTop: "1px solid #e5e7eb" }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{it.patientNameImported || <span style={{ color: "#9ca3af" }}>—</span>}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{it.externalId}</div>
                </td>
                <td style={td}>{it.phone || "—"}</td>
                <td style={td}>{fmtDate(it.appointmentDatetime)}</td>
                <td style={td}>{statusBadge(it.matchingStatus)}</td>
                <td style={{ ...td, fontSize: 12, color: "#6b7280", maxWidth: 320 }}>{it.matchingReason}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button
                    data-testid={`match-btn-${it.externalId}`}
                    onClick={() => setMatchTarget(it)}
                    style={btnPrimary}>
                    <UserCheck size={14} /> Associer
                  </button>
                  <button
                    data-testid={`ignore-btn-${it.externalId}`}
                    onClick={() => handleIgnore(it)}
                    style={btnSecondary}>
                    <X size={14} /> Ignorer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Match modal */}
      {matchTarget && (
        <MatchPatientModal
          appt={matchTarget}
          onClose={() => setMatchTarget(null)}
          onConfirm={(pid) => handleMatchSelected(matchTarget, pid)}
        />
      )}
    </div>
  );
}

const th = { padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em" };
const td = { padding: "12px 14px", verticalAlign: "top" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", marginRight: 6,
                     background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 };
const btnSecondary = { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px",
                       background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 13 };


// ----------------------------------------------------------------------
// Modal — search patient + confirm
// ----------------------------------------------------------------------

function MatchPatientModal({ appt, onClose, onConfirm }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  // Show suggested candidates first
  useEffect(() => {
    setResults((appt.possiblePatients || []).map((c) => ({ ...c, isSuggested: true })));
  }, [appt]);

  const doSearch = async () => {
    const q = query.trim();
    if (q.length < 2) {
      toast.info("Tapez au moins 2 caractères");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/patients", { params: { search: q, limit: 20 } });
      const list = data?.items || data || [];
      setResults(list.map((p) => ({
        id: p.id || p._id,
        name: `${p.firstName || ""} ${p.lastName || ""}`.trim(),
        phone: p.phone,
        cin: p.cin,
        matchHint: "Recherche manuelle",
      })));
    } catch (e) {
      toast.error("Recherche impossible");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="match-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
                  alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
         onClick={onClose}>
      <div style={{ background: "white", borderRadius: 12, maxWidth: 600, width: "100%",
                    maxHeight: "85vh", display: "flex", flexDirection: "column" }}
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Associer un patient</h3>
          <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
            <strong>{appt.patientNameImported || "Sans nom"}</strong> · {appt.phone || "—"} · {appt.externalId}
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input
              data-testid="patient-search-input"
              autoFocus
              type="text"
              placeholder="Chercher par nom ou téléphone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
              style={{ width: "100%", padding: "8px 8px 8px 32px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14 }}
            />
          </div>
          <button data-testid="patient-search-btn" onClick={doSearch} disabled={searching}
                  style={{ ...btnPrimary, marginRight: 0 }}>
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Chercher
          </button>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
          {results.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280", fontSize: 14 }}>
              Aucun candidat suggéré. Lancez une recherche par nom ou téléphone.
            </div>
          )}
          {results.map((p) => (
            <label
              key={p.id}
              data-testid={`candidate-${p.id}`}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                       borderRadius: 8, cursor: "pointer",
                       background: selectedId === p.id ? "#eff6ff" : "transparent" }}>
              <input type="radio" name="patient-select" value={p.id}
                     checked={selectedId === p.id}
                     onChange={() => setSelectedId(p.id)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{p.name || "(sans nom)"}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {p.phone || "—"} · CIN: {p.cin || "—"}
                  {p.matchHint && <span style={{ marginLeft: 8, color: "#2563eb" }}>· {p.matchHint}</span>}
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button data-testid="match-cancel-btn" onClick={onClose} style={btnSecondary}>Annuler</button>
          <button
            data-testid="match-confirm-btn"
            onClick={() => selectedId && onConfirm(selectedId)}
            disabled={!selectedId}
            style={{ ...btnPrimary, opacity: selectedId ? 1 : 0.5, cursor: selectedId ? "pointer" : "not-allowed" }}>
            <UserCheck size={14} /> Confirmer l'association
          </button>
        </div>
      </div>
    </div>
  );
}
